import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import LanguageSelector from '../LanguageSelector';
import { LinkedInLink } from '../LinkedInLink';
import { 
  LayoutDashboard, 
  Plane, 
  Map, 
  CreditCard, 
  LogOut,
  ChevronRight,
  Shield,
  Settings,
  BookOpen,
  HelpCircle,
  Globe2,
  Mail
} from 'lucide-react';

export const Sidebar = ({ isOpen, onClose, collapsed = false }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('dashboard.title') },
    { to: '/drones', icon: Plane, label: t('drones.title') },
    { to: '/missions', icon: Map, label: t('missions.title') },
    { to: '/subscription', icon: CreditCard, label: t('subscription.title') },
  ];

  // Add admin link if user is admin
  if (user?.role === 'admin') {
    navItems.push({ to: '/admin', icon: Settings, label: t('nav.admin') });
  }

  // Secondary "Resources" links (public pages still useful while logged in)
  const resourceItems = [
    { to: '/blog', icon: BookOpen, label: t('nav.blog') },
    { to: '/faq', icon: HelpCircle, label: t('nav.faq') },
    { to: '/regulations/france', icon: Globe2, label: t('sidebar.countries') },
    { to: '/contact', icon: Mail, label: t('sidebar.contact') },
  ];

  const legalLinks = [
    { to: '/mentions-legales', label: t('sidebar.legal') },
    { to: '/confidentialite', label: t('sidebar.privacy') },
    { to: '/cgu', label: t('sidebar.cgu') },
    { to: '/cgv', label: t('sidebar.cgv') },
    { to: '/cookies', label: t('sidebar.cookies') },
  ];

  const getPlanLabel = () => {
    switch (user?.subscription_status) {
      case 'solo': return t('subscription.solo');
      case 'partner': return t('subscription.partner');
      default: return t('subscription.free');
    }
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside 
        className={`
          fixed top-0 left-0 h-full bg-[#1b2236] text-white z-40
          transform transition-all duration-200 ease-out
          flex flex-col
          ${collapsed ? 'lg:w-16' : 'lg:w-64'}
          ${isOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo — clicking always returns to the public homepage
            (even when authenticated). Behaviour matches the convention
            used by Stripe / Vercel / Linear consoles. */}
        <div className={`p-4 border-b border-slate-700 ${collapsed ? 'lg:px-3' : 'p-6'}`}>
          <Link
            to="/"
            className={`flex items-center transition-opacity hover:opacity-80 ${collapsed ? 'lg:justify-center' : 'gap-3'}`}
            data-testid="sidebar-logo-home"
            aria-label={t('nav.backToHome', 'Retour à l\'accueil')}
          >
            {collapsed ? (
              <img src="/favicon-192.png" alt="Drone Maps" className="lg:w-12 lg:h-12 w-12 h-12 rounded flex-shrink-0" />
            ) : (
              <img src="/logo-landscape.png" alt="Drone Maps" className="h-16 w-auto" />
            )}
          </Link>
        </div>

        {/* User Info */}
        <div className={`p-4 border-b border-slate-700 ${collapsed ? 'lg:px-2' : ''}`}>
          <div className={`flex items-center ${collapsed ? 'lg:justify-center' : 'gap-3'}`}>
            <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className={`flex-1 min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <div className={`mt-3 ${collapsed ? 'lg:hidden' : ''}`}>
            <span className={`
              inline-flex items-center px-2 py-1 rounded text-xs font-medium font-mono uppercase tracking-wider
              ${user?.subscription_status === 'solo' 
                ? 'bg-sky-500/20 text-sky-300' 
                : user?.subscription_status === 'partner'
                ? 'bg-emerald-500/20 text-emerald-300'
                : 'bg-slate-700 text-slate-300'}
            `}>
              {getPlanLabel()}
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className={`p-4 flex-1 overflow-y-auto ${collapsed ? 'lg:p-2' : ''}`}>
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={onClose}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    transition-colors duration-150
                    ${collapsed ? 'lg:justify-center lg:px-2' : ''}
                    ${isActive 
                      ? 'bg-sky-500 text-white' 
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'}
                  `}
                  data-testid={`nav-${item.to.slice(1)}`}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className={`${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                  <ChevronRight className={`w-4 h-4 ml-auto opacity-50 ${collapsed ? 'lg:hidden' : ''}`} />
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Resources section (Blog, FAQ, Country regulations) */}
          <div className={`mt-6 pt-4 border-t border-slate-700/60 ${collapsed ? 'lg:mt-3 lg:pt-3' : ''}`}>
            <p
              className={`px-3 mb-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold ${collapsed ? 'lg:hidden' : ''}`}
            >
              {t('sidebar.resources')}
            </p>
            <ul className="space-y-1">
              {resourceItems.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onClose}
                    className={`
                      flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium
                      text-slate-400 hover:bg-slate-800 hover:text-white
                      transition-colors duration-150
                      ${collapsed ? 'lg:justify-center lg:px-2' : ''}
                    `}
                    data-testid={`sidebar-resource-${item.to.slice(1).replace(/\//g, '-')}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span className={`${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Legal mini-footer */}
        <div className={`px-4 pt-3 pb-2 border-t border-slate-700 ${collapsed ? 'lg:hidden' : ''}`}>
          <ul className="flex flex-wrap gap-x-2 gap-y-1 text-[10px] text-slate-500 leading-tight">
            {legalLinks.map((item, idx) => (
              <li key={item.to} className="flex items-center">
                <Link
                  to={item.to}
                  onClick={onClose}
                  className="hover:text-slate-300 transition-colors"
                  data-testid={`sidebar-legal-${item.to.slice(1)}`}
                >
                  {item.label}
                </Link>
                {idx < legalLinks.length - 1 && <span className="ml-2 text-slate-700">·</span>}
              </li>
            ))}
          </ul>
        </div>

        {/* Language Selector + social follow */}
        <div className={`p-4 border-t border-slate-700 space-y-3 ${collapsed ? 'lg:hidden' : ''}`}>
          <LanguageSelector />
          <div className="pt-1">
            <LinkedInLink
              className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-xs transition-colors"
              label="Suivre sur LinkedIn"
            />
          </div>
        </div>

        {/* Logout */}
        <div className={`p-4 border-t border-slate-700 ${collapsed ? 'lg:p-2' : ''}`}>
          <button
            onClick={handleLogout}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
              text-slate-400 hover:bg-slate-800 hover:text-white transition-colors
              ${collapsed ? 'lg:justify-center lg:px-2' : ''}
            `}
            data-testid="logout-btn"
            title={collapsed ? t('nav.logout') : undefined}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span className={`${collapsed ? 'lg:hidden' : ''}`}>{t('nav.logout')}</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
